import { Component, computed, effect, inject, OnInit, signal, Signal } from '@angular/core';
import { FacetFormComponent } from 'app/my-history/shared/facet-form.component';
import { ButtonComponent } from 'app/shared/button/button.component';
import { IIclData, IMentionUser, IStory, IStoryPayload, StoryForm, StoryUnit } from '../story';
import { FormArray, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { StoryService } from '../story.service';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { TextButtonComponent } from 'app/shared/button/text-button/text-button.component';
import { InlineDialogTitleComponent } from 'app/shared/dialog/inline-dialog/inline-dialog-title/inline-dialog-title.component';
import { FileUploadComponent } from 'app/shared/form/file-upload/file-upload.component';
import { FormGridComponent } from 'app/shared/form/form-grid/form-grid.component';
import { InputComponent } from 'app/shared/form/input/input.component';
import { UserFacet } from 'app/my-history/facet/facet';
import { StoryStatus } from '../story-status';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChapterService } from 'app/my-history/chapter/chapter.service';
import { FacetModel } from 'app/my-history/facet/facet.model';
import { FacetGridCompactContainerComponent } from 'app/my-history/facet/facet-grid-compact-container/facet-grid-compact-container.component';
import { provideIcons } from '@ng-icons/core';
import { heroChevronRight, heroSpeakerWave } from '@ng-icons/heroicons/outline';
import { FileUploadPreviewComponent } from 'app/shared/form/file-upload/file-upload-preview/file-upload-preview.component';
import { StateType, VoiceRecordingComponent } from '../../../shared/form/voice-recording/voice-recording.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { StoryPreviewComponent } from '../story-preview/story-preview.component';
import { FormUtils } from 'app/shared/form/form-utils';
import { HttpUtils } from 'app/shared/http-utils';
import { DialogComponent } from 'app/shared/dialog/dialog.component';
import { IPreviewUrls } from 'app/shared/form/file.model';
import { forkJoin, map } from 'rxjs';
import { DatePickerType, IDateData } from 'app/shared/date.const';
import { DatepickerComponent } from 'app/shared/datepicker/datepicker.component';
import { AuthService } from '../../../auth/auth.service';
import { MatDialogRef } from '@angular/material/dialog';
import { StoryFilterService } from '../story-filter.service';
import { ShareStoryComponent } from '../share-story/share-story.component';
import { IconComponent } from '../../../shared/icon/icon.component';
import { MentionPeopleScreenComponent } from '../mention-people-screen/mention-people-screen.component';
import { MultiFacetSelectionComponent } from '../../facet/multi-facet-selection/multi-facet-selection.component';
import { FacetService } from '../../facet/facet.service';

@Component({
  selector: 'app-story-form',
  standalone: true,
  imports: [
    FormGridComponent,
    ReactiveFormsModule,
    InputComponent,
    FileUploadComponent,
    ButtonComponent,
    TextButtonComponent,
    NgFor,
    InlineDialogTitleComponent,
    FacetGridCompactContainerComponent,
    FileUploadPreviewComponent,
    VoiceRecordingComponent,
    MatCheckboxModule,
    StoryPreviewComponent,
    FormsModule,
    DialogComponent,
    DatepickerComponent,
    ShareStoryComponent,
    IconComponent,
    MentionPeopleScreenComponent,
    NgClass,
    MultiFacetSelectionComponent,
    NgIf,
  ],
  templateUrl: './story-form.component.html',
  styleUrls: ['./story-form.component.scss'],
  providers: [ChapterService, provideIcons({ heroChevronRight, heroSpeakerWave })],
})
export class StoryFormComponent extends FacetFormComponent<IStory, StoryForm, IStoryPayload> implements OnInit {
  override facetCrudService = inject(StoryService);
  private storyService = inject(StoryService);
  private dialogRef = inject(MatDialogRef<StoryFormComponent>);
  private storyFilterService = inject(StoryFilterService);
  protected facetService = inject(FacetService);
  user = inject(AuthService).user;

  private story: IStory | undefined;
  private id: string | undefined;
  private imageToRemove: string[] = [];

  override fileApiUrl: string = HttpUtils.getApiUrl('/files/stories');
  private audioApiUrl: string = HttpUtils.getApiUrl('/files/audio');

  isDraft = false;
  textContent: string = '';
  maxLengthTextarea: number = 4000;

  storyAcl = signal<IIclData>({
    friends: [],
    acquaintance: false,
    communities: null,
  });

  shareWithFriendsDisplay = signal(false);
  audioState = signal<StateType>('idle');
  previewUrls = signal<IPreviewUrls[]>([]);
  isPreview = signal(false);

  validateDatePicker = signal(false);
  transcript = signal('');
  isNewAudioRecorded = signal(false);
  audioUrl = signal<string | undefined>(undefined);
  isUploaded = signal(false);
  selectedPicker = signal<DatePickerType | null>(null);
  userYears = computed(() => {
    const userDate = new Date(this.user().birthDate);
    const today = new Date();
    return today.getFullYear() - userDate.getFullYear();
  });
  dateData: IDateData | undefined;

  override selected: Signal<IStory | null> = this.facetCrudService.selectedFacet;
  override selectedImage: Signal<string | null | string[]> = this.facetCrudService.selectedFacetImage;

  isSelectFacets = signal(false);
  formArray = computed(() => this.form().get('facets') as FormArray);
  formGroup = computed(() => this.formArray().at(0));
  isNextButton = computed(() => this.isSelectFacets());
  submitButtonText = computed(() => {
    let buttonTitle = this.isNextButton() ? 'Next' : this.isPreview() ? 'Publish' : 'Preview';

    // in edit mode preview is absent
    if (!this.isNextButton() && this.mode() === 'Edit') {
      buttonTitle = 'Save Changes';
    }

    return buttonTitle;
  });

  // ===Mention People Section START
  protected mentionPeopleScreen = false;
  currentlyMentionedUsers = new Map<number, IMentionUser>();
  mentionsToRemove = new Set<number>();
  mentionsToAdd = new Set<number>();
  displayedMentions = new Map<number, IMentionUser>();

  get mentionUsersArr() {
    return Array.from(this.displayedMentions.entries());
  }

  // === Mention People Section END
  // Multi Facets Selection Section Start
  addMoreFacetsScreen = signal(false);
  editFacetScreen = signal(false);
  editFacet: UserFacet | undefined = undefined;

  get allInCurrentFacet(): FacetModel[] | null {
    const facets = this.currentSelectedFacets;

    if (!facets || !facets.length || !this.editFacet) {
      return null;
    }

    return this.allUnitsForFacets[this.editFacet.id];
  }

  //return all selected unit on current editing facet
  get selectedInCurrentFacet(): FacetModel[] | null {
    const facets: UserFacet[] = this.selectedFacets();

    if (!facets || !facets.length || !this.editFacet) {
      return null;
    }

    const facet = facets.find((f) => f.id === this.editFacet?.id);
    return facet?.units ?? null;
  }

  allFacets = this.facetService.userNoChapterFacetTypes();
  availableFacets: UserFacet[] = this.facetService.userNoChapterFacetTypes();
  selectedFacets = signal<UserFacet[]>([]);
  additionallySelectedFaces = signal<UserFacet[]>([]);
  selectedFacetAliases = computed(() => this.selectedFacets().map((type) => type.alias) || []);
  chooseFacetUnitsPage = signal<number | null>(null);

  // це обєкт в якому ключ це вибраний фасет з pills а value це всі юніти які є в цьому фасеті
  allUnitsForFacets: { [name: string]: FacetModel[] } = {};
  selectFacetsScreen = signal(this.mode() === 'Create');

  get selectedFacetUnits(): FacetModel[] | null {
    const facets = this.currentSelectedFacets;
    const pageIndex = this.chooseFacetUnitsPage() || 0;

    if (
      !facets ||
      !facets.length ||
      pageIndex >= facets.length ||
      !facets[pageIndex].units ||
      !facets[pageIndex].units?.length
    ) {
      return null;
    }

    return facets[pageIndex].units;
  }

  get currentSelectedFacets(): UserFacet[] {
    if (this.addMoreFacetsScreen()) {
      return this.additionallySelectedFaces();
    }
    return this.selectedFacets();
  }

  // Multi Facets Selection Section End

  constructor() {
    super();

    effect(() => {
      if (this.facetId()) {
        this.facetCrudService.addStoryAcl(this.facetId() as string, this.storyAcl()).subscribe();

        if (this.mentionsToAdd.size) {
          this.storyService.addMentionBatch(this.facetId() as string, [...this.mentionsToAdd]).subscribe(() => {
            this.mentionsToAdd.clear();
          });
        }
      }
    });

    effect(() => this.mode() === "Create" ? this.form() : undefined);

  }

  override ngOnInit() {
    super.ngOnInit();
  }

  onDateChange(response: IDateData) {
    if (response) {
      if (response.type) {
        this.selectedPicker.set(response.type as DatePickerType);
      }

      if (response.dateComponents) {
        this.formGroup()?.get('dateComponents')?.setValue(response.dateComponents);
      } else {
        this.formGroup()?.get('dateComponents')?.setValue(null);
      }
    }

    this.dateData = response;
  }

  setAudioState(state: StateType) {
    if (state === 'denied') {
      this.audioState.set(state);
    }
  }

  onRemoveImage(index: number) {
    const imagesArray = this?.formArray()?.at(0)?.get('imageFile')?.value || [];

    if (imagesArray.length) {
      const image = imagesArray[index];
      const newImages = imagesArray.filter((_: IPreviewUrls | File, i: number) => index !== i);
      const previewUrls = this.previewUrls();

      this.previewUrls.set(previewUrls.filter((_: IPreviewUrls, i: number) => index !== i));

      if (image.url) {
        this.imageToRemove.push(image.name);
      }

      this.formArray().at(0).get('imageFile')?.setValue(newImages);
    }
  }

  setPreviewUrl(previewUrls: IPreviewUrls[]) {
    if (!previewUrls || typeof previewUrls === 'string') return;

    if (previewUrls.length) {
      this.previewUrls.set(previewUrls);
    }
  }

  updateDescription(event: Event): void {
    const target = event.target as HTMLTextAreaElement;
    this.textContent = target?.value || '';
  }

  mentionPeople() {
    this.mentionPeopleScreen = true;
  }

  onMentionFriends(users: { selected: IMentionUser[]; unselected: IMentionUser[] }) {
    users.unselected.forEach((user) => {
      if (this.mode() === 'Create') {
        this.mentionsToAdd.delete(user.id);
        if (this.displayedMentions.has(user.id)) {
          this.displayedMentions.delete(user.id);
        }
        return;
      }
      if (this.currentlyMentionedUsers.has(user.id)) {
        this.mentionsToRemove.add(user.id);
        this.mentionsToAdd.delete(user.id);

        if (this.displayedMentions.has(user.id)) {
          this.displayedMentions.delete(user.id);
        }
      }
    });

    users.selected.forEach((user) => {
      if (!this.currentlyMentionedUsers.has(user.id)) {
        this.mentionsToAdd.add(user.id);
        this.mentionsToRemove.delete(user.id);
        this.displayedMentions.set(user.id, user);
      }
    });

    this.facetCrudService.setMentionsToRemove(this.mentionsToRemove);
    this.facetCrudService.setMentionsToAdd(this.mentionsToAdd);
  }

  override createFormGroup(facet: IStory | null): FormGroup {
    let id;
    let title;
    let content;
    let units;
    let status;
    let dateComponents;
    const category = this.storyFilterService.selectedFacet();

    // pre set data if stories by facet opened
    if (category) {
      dateComponents = {
        year: new Date(category.start).getFullYear(),
      };

      this.dateData = {
        dateComponents,
        type: DatePickerType.YEAR,
      };

      units = [category.lense.id];
    }

    if (facet) {
      id = facet.id;
      title = facet.title;
      content = facet.content;
      units = facet.units.map((u) => u.lenseId);
      status = facet.status;
      dateComponents = facet.dateComponents;

      if (facet?.mentionedUsers?.length) {
        this.currentlyMentionedUsers = new Map(facet.mentionedUsers.map((obj) => [obj.id, obj]));
        this.displayedMentions = new Map(facet.mentionedUsers.map((obj) => [obj.id, obj]));
      }

      this.story = facet;
      this.id = facet.id;
      this.textContent = facet.content;

      this.dateData = {
        dateComponents,
        type: DatePickerType.YEAR,
      };

      if (facet.dateComponents.month !== undefined) {
        this.dateData.type = DatePickerType.MONTH;
      }

      if (facet.dateComponents.day) {
        this.dateData.type = DatePickerType.DAY;
      }
    }

    queueMicrotask(() => {
      if (category) {
        // this.selectFacetsScreen.set(true);
        this.chapterService.setSelectedFacetType(category.lense);
        this.chapterService
          .getAll(category.lense.id, true)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((models) => {
            if (models && models.length == 1) {
              this.selectFacetsScreen.set(false);
              this.allUnitsForFacets[category.lense.id] = models;

              const facet = this.allFacets.find((f) => f.id === category.lense.id);

              if (facet) {
                facet.units = models;
                this.selectedFacets.set([facet]);
                this.availableFacets = this.updateAvailableFacetsList();
              }
            } else {
              this.selectFacetsScreen.set(this.mode() === 'Create');
            }
          });
      }

      if (facet) {
        this.setPreviewUrl(this.facetCrudService.getStoryImages(facet as IStory));
        this.formArray().at(0)?.get('imageFile')?.setValue(this.previewUrls());

        if (facet.audio && facet.audio.data && facet.audio.data.length) {
          this.audioUrl.set(facet.audio.data[0].url);
          this.isUploaded.set(true);

          this.formArray().at(0)?.get('audio')?.setValue(this.audioUrl());
        }

        if (facet.units && facet.units.length) {
          const uniqueLenses = [
            ...new Set(
              facet.units
                .map((obj) => this.allFacets.find((f) => f.id === obj.lenseId))
                .filter((value) => value !== undefined),
            ),
          ];

          if (uniqueLenses.length) {
            const requests = uniqueLenses.map((lense) =>
              this.chapterService
                .getAll(lense.id, true)
                .pipe(map((facetModel: FacetModel[]) => ({ lense, facetModel }))),
            );

            forkJoin(requests)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe((results) => {
                results.forEach(({ lense, facetModel }) => {
                  this.allUnitsForFacets[lense.id] = facetModel;
                  const selectedUnitIds = facet.units.filter((f) => f.lenseId === lense.id);

                  lense.units = selectedUnitIds
                    .map((obj) => {
                      return facetModel.find((facet) => facet.id === obj.id);
                    })
                    .filter((value) => value !== undefined);
                });

                this.selectedFacets.set(uniqueLenses);
                this.selectFacetsScreen.set(false);
                this.addMoreFacetsScreen.set(false);
                this.chooseFacetUnitsPage.set(null);
                this.isPreview.set(false);
                this.availableFacets = this.updateAvailableFacetsList();
              });
          }
        }
      }
    });

    return this.fb.nonNullable.group({
      id: [id],
      title: [title, Validators.required],
      content: [content, [Validators.required, Validators.maxLength(this.maxLengthTextarea)]],
      units: [units],
      status: [status],
      dateComponents: [dateComponents, Validators.required],
      imageFile: [],
      audio: [],
    });
  }

  override getSubmitPayload(facet: StoryForm): IStoryPayload {
    const status = this.isDraft ? StoryStatus.draft : StoryStatus.publish;
    const { title, content, start, dateComponents } = facet;
    const id = facet.id ? facet.id : undefined;
    const unitsToSave: StoryUnit[] = [];
    const selectedFacets = this.selectedFacets();

    if (selectedFacets && selectedFacets.length) {
      selectedFacets.forEach((facet) => {
        if (facet.units && facet.units.length) {
          facet.units.forEach((unit) => {
            unitsToSave.push({
              lenseId: facet.id,
              id: unit.id,
            });
          });
        }
      });
    }

    return {
      id,
      title,
      content,
      units: unitsToSave,
      status,
      start,
      dateComponents,
    };
  }

  override onSubmitted(facet: IStory): void {
    super.onSubmitted(facet);

    this.dialogRef.close(facet);
    this.storyService.getStoriesData();
  }

  override onSubmit(): void {
    if (this.isDraft) {
      this.remove();
      super.onSubmit();
      return;
    }

    this.validateDatePicker.set(true);

    const { formValidityCheck } = FormUtils;
    if (!formValidityCheck(this.form())) {
      return;
    }

    if (this.isSelectFacets()) {
      this.back();
    } else if (!this.isPreview() && this.mode() === 'Create') {
      this.preview();
    } else {
      this.isDraft = this.story?.status === StoryStatus.draft;
      this.remove();
      super.onSubmit();
      this.isDraft = false;
    }
  }

  convertVoiceToText() {
    const contentControl = this.formGroup()?.get('content');
    if (!contentControl) return;

    contentControl.setValue(`${contentControl.value ? contentControl.value + ' ' : ''}${this.transcript()}`);

    this.textContent = contentControl.value;

    this.form().updateValueAndValidity();
    this.transcript.set('');
    this.isNewAudioRecorded.set(false);
  }

  onTranscriptChange(transcript: string) {
    this.transcript.set(transcript);
  }

  onAudioRecorded(recorded: boolean) {
    this.isNewAudioRecorded.set(recorded);
    // this.audioUrl.set((this.formGroup().get('audio')?.value))
  }

  preview() {
    this.isPreview.set(true);
  }

  getUnitsForFacet(facetId: number) {
    return this.allUnitsForFacets[facetId.toString()] || [];
  }

  selectUnitsForFacet() {
    if (!Object.keys(this.allUnitsForFacets).length) {
      this.chooseFacetUnitsPage.set(null);
      this.addMoreFacetsScreen.set(false);
      return this.selectFacetsScreen.set(false);
    }

    if (this.selectFacetsScreen() && Object.keys(this.allUnitsForFacets).length) {
      if (this.chooseFacetUnitsPage() === null) {
        this.chooseFacetUnitsPage.set(0);
      } else {
        if ((this.chooseFacetUnitsPage() as number) < this.currentSelectedFacets.length - 1) {
          const newPage = (this.chooseFacetUnitsPage() as number) + 1;

          this.chooseFacetUnitsPage.set(newPage);
        } else {
          if (this.addMoreFacetsScreen()) {
            const additionalFacets = this.additionallySelectedFaces();
            const selectedFacets = this.selectedFacets();

            additionalFacets.forEach((facet) => {
              if (facet && facet.units && facet.units.length) {
                selectedFacets.push(facet);
              }
            });

            this.selectedFacets.set(selectedFacets);
          }

          let filteredFacets: UserFacet[] = [];
          const selectedFacets = this.selectedFacets();

          // remove facets without checked units from selected array
          if (selectedFacets && selectedFacets.length) {
            filteredFacets = selectedFacets.filter((facet) => facet.units && facet.units.length);
          }

          this.selectedFacets.set(filteredFacets);

          // reset page status
          this.availableFacets = this.updateAvailableFacetsList();
          this.selectFacetsScreen.set(false);
          this.addMoreFacetsScreen.set(false);
        }
      }
    } else {
      this.selectFacetsScreen.set(false);
      this.addMoreFacetsScreen.set(false);
    }
  }

  skipFacets() {
    this.selectFacetsScreen.set(false);
    this.allUnitsForFacets = {};
    this.selectedFacets.set([]);
    this.additionallySelectedFaces.set([]);

    this.availableFacets = this.updateAvailableFacetsList();
    this.isPreview.set(false);
    this.addMoreFacetsScreen.set(false);
  }

  addMoreFacets() {
    this.selectFacetsScreen.set(true);
    this.chooseFacetUnitsPage.set(null);

    this.availableFacets = this.updateAvailableFacetsList();
    this.addMoreFacetsScreen.set(true);
  }

  updateAvailableFacetsList(): UserFacet[] {
    const selectedFacets = this.selectedFacets();

    if (!this.allFacets) {
      return [];
    }

    if (!selectedFacets || selectedFacets.length === 0) {
      return this.allFacets;
    }

    const selectedIds = new Set(selectedFacets.map((facet) => facet.id));
    return this.allFacets.filter((facet) => !selectedIds.has(facet.id));
  }

  finishEditFacet() {
    const selectedFacets = this.selectedFacets();

    if (selectedFacets && selectedFacets.length && this.editFacet) {
      const facet = selectedFacets.find((f) => f.id === this.editFacet?.id);

      if (facet && (!facet.units || !facet.units.length)) {
        const filteredFacets = selectedFacets.filter((f) => f.id !== this.editFacet?.id);
        this.selectedFacets.set(filteredFacets);
      }
    }

    this.availableFacets = this.updateAvailableFacetsList();
    this.editFacetScreen.set(false);
    this.editFacet = undefined;
  }

  editFacetModel(facet: UserFacet) {
    this.editFacetScreen.set(true);
    this.isSelectFacets.set(false);
    this.isPreview.set(false);
    this.chooseFacetUnitsPage.set(null);

    this.editFacet = facet;
  }

  backToPrevious(): void {
    if (this.selectFacetsScreen() && Object.keys(this.allUnitsForFacets).length) {
      if (this.chooseFacetUnitsPage() !== null) {
        const newPage = (this.chooseFacetUnitsPage() as number) - 1;
        this.chooseFacetUnitsPage.set(newPage >= 0 ? newPage : null);
      }
    }
  }

  next() {
    this.selectFacetsScreen.set(true);
  }

  back() {
    this.isSelectFacets.set(false);
    this.isPreview.set(false);
    this.mentionPeopleScreen = false;
  }

  backToPreview() {
    this.isPreview.set(true);
    this.shareWithFriendsDisplay.set(false);
  }

  updateAcl(acl: IIclData) {
    this.storyAcl.set(acl);
    this.facetCrudService.setAcl(this.storyAcl());
  }

  toggleFacetModel(facetUnit: FacetModel) {
    if (facetUnit && facetUnit.lense.id) {
      const selectedFacets = this.currentSelectedFacets;

      if (selectedFacets && selectedFacets.length) {
        const updatedFacets: UserFacet[] = selectedFacets.map((facet) => {
          if (facet.id === facetUnit.lense.id) {
            facet.units = facet.units?.length ? facet.units : [];

            const idx = facet.units.findIndex((i) => i.id === facetUnit.id);

            if (idx > -1) {
              facet.units.splice(idx, 1);
            } else {
              facet.units.push(facetUnit);
            }
          }

          return facet;
        });

        if (this.addMoreFacetsScreen()) {
          this.additionallySelectedFaces.set(updatedFacets);
        } else {
          this.selectedFacets.set(updatedFacets);
        }
      }
    }
  }

  deleteFacetModel(facet: UserFacet) {
    if (facet.units) {
      delete facet.units;
    }

    const selectedFacets = this.selectedFacets();
    this.selectedFacets.set(selectedFacets.filter((f) => f.id !== facet.id));
    this.availableFacets = this.updateAvailableFacetsList();
  }

  onSelectedFacetChanged(facets: UserFacet[]): void {
    if (facets && facets.length) {
      facets.forEach((facet) => {
        if (!this.allUnitsForFacets[facet.id.toString()]) {
          this.chapterService
            .getAll(facet.id, true)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((f: FacetModel[]) => {
              this.allUnitsForFacets[facet.id.toString()] = f;
            });
        }
      });

      // Add to selected
      if (this.addMoreFacetsScreen()) {
        this.additionallySelectedFaces.set(facets);
      } else {
        this.selectedFacets.set(facets);
      }
    }
  }

  onSaveDraft() {
    this.isDraft = true;
    this.onSubmit();
    this.isDraft = false;
  }

  onRemoveAudio(uploaded: boolean) {
    this.isUploaded.set(uploaded);
  }

  remove() {
    let storyAudio;
    const currentAudio = this.formArray().at(0)?.get('audio')?.value;

    if (this?.story?.audio?.data.length) {
      storyAudio = this?.story?.audio?.data[0].url;
    }

    if (storyAudio && storyAudio !== currentAudio) {
      this.filesService.removeFile(this.audioApiUrl, this.story?.id as string).subscribe();
    }

    if (this.imageToRemove.length) {
      forkJoin(
        this.imageToRemove.map((imgName) => {
          return this.facetCrudService.removeImage(this.fileApiUrl, imgName, this.id as string);
        }),
      ).subscribe(() => {
        this.imageToRemove = [];
      });
    }

    if (this.mentionsToRemove.size) {
      this.mentionsToRemove.forEach((userId) => {
        this.storyService.removeMention(this.story?.id as string, userId).subscribe(() => {
          this.mentionsToRemove.delete(userId);
          this.currentlyMentionedUsers.delete(userId);
        });
      });
    }
  }

  showShareWithFriendsScreen(display: boolean) {
    this.shareWithFriendsDisplay.set(display);
    this.isPreview.set(false);
  }

  protected readonly Number = Number;
}
